import React, { useState } from 'react';
import { Save, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { apiCall, useAdminData, ImageUploader } from './_helpers';

const COL_TYPES = [
  { value: 'links', label: 'Links' },
  { value: 'hours', label: 'Hours (auto from Restaurant Info)' },
  { value: 'contact', label: 'Contact (auto from Restaurant Info)' },
  { value: 'social', label: 'Social Icons (auto from Restaurant Info)' },
];

const FooterManager = ({ onMessage }) => {
  const [footer, setFooter, refetch] = useAdminData('footer', {});
  const [saving, setSaving] = useState(false);

  const cols = footer.columns || [];
  const updateCol = (idx, patch) => setFooter({ ...footer, columns: cols.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const removeCol = (idx) => setFooter({ ...footer, columns: cols.filter((_, i) => i !== idx) });
  const addCol = () => setFooter({ ...footer, columns: [...cols, { title: 'New Column', type: 'links', links: [] }] });
  const moveCol = (idx, dir) => {
    const next = [...cols]; const t = idx + dir;
    if (t < 0 || t >= next.length) return;
    [next[idx], next[t]] = [next[t], next[idx]];
    setFooter({ ...footer, columns: next });
  };

  const updateColLink = (cIdx, lIdx, patch) => {
    const newCols = cols.map((c, i) => {
      if (i !== cIdx) return c;
      const newLinks = (c.links || []).map((l, j) => (j === lIdx ? { ...l, ...patch } : l));
      return { ...c, links: newLinks };
    });
    setFooter({ ...footer, columns: newCols });
  };
  const addColLink = (cIdx) => updateCol(cIdx, { links: [...(cols[cIdx].links || []), { label: '', href: '' }] });
  const removeColLink = (cIdx, lIdx) => updateCol(cIdx, { links: (cols[cIdx].links || []).filter((_, j) => j !== lIdx) });

  const bottomLinks = footer.bottom_links || [];
  const updateBottom = (idx, patch) => setFooter({ ...footer, bottom_links: bottomLinks.map((l, i) => (i === idx ? { ...l, ...patch } : l)) });
  const removeBottom = (idx) => setFooter({ ...footer, bottom_links: bottomLinks.filter((_, i) => i !== idx) });
  const addBottom = () => setFooter({ ...footer, bottom_links: [...bottomLinks, { label: '', href: '#' }] });

  const save = async () => {
    setSaving(true);
    try { await apiCall('PUT', 'footer', footer); onMessage('success', 'Footer saved'); refetch(); }
    catch (e) { onMessage('error', e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-md">
        <h2 className="text-2xl font-bold mb-6">Footer</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Logo</label>
            <ImageUploader currentImage={footer.logo_url} onUpload={(url) => setFooter({ ...footer, logo_url: url })} />
            <input type="text" value={footer.logo_url || ''} onChange={(e) => setFooter({ ...footer, logo_url: e.target.value })} placeholder="or paste URL" className="w-full border rounded px-3 py-2 text-sm mt-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tagline</label>
            <input type="text" value={footer.tagline || ''} onChange={(e) => setFooter({ ...footer, tagline: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={footer.show_newsletter_button !== false} onChange={(e) => setFooter({ ...footer, show_newsletter_button: e.target.checked })} />
            <span className="text-sm">Show "Join Newsletter" button (in social column)</span>
          </label>
          <div>
            <label className="block text-sm font-medium mb-1">Newsletter Button Label</label>
            <input type="text" value={footer.newsletter_button_label || ''} onChange={(e) => setFooter({ ...footer, newsletter_button_label: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Copyright Text (use {'{YEAR}'} for auto-year)</label>
            <input type="text" value={footer.copyright_text || ''} onChange={(e) => setFooter({ ...footer, copyright_text: e.target.value })} className="w-full border rounded px-3 py-2" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Columns</h3>
          <button onClick={addCol} className="bg-[#C9A86C] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#B8956A]"><Plus size={16} /> Add Column</button>
        </div>
        <div className="space-y-3">
          {cols.map((col, cIdx) => (
            <div key={cIdx} className="border rounded-lg p-3">
              <div className="flex items-center gap-2 mb-3">
                <input type="text" value={col.title} onChange={(e) => updateCol(cIdx, { title: e.target.value })} placeholder="Column Title" className="flex-1 border rounded px-3 py-2 text-sm" />
                <select value={col.type || 'links'} onChange={(e) => updateCol(cIdx, { type: e.target.value })} className="border rounded px-3 py-2 text-sm">
                  {COL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button onClick={() => moveCol(cIdx, -1)} className="p-2 hover:bg-gray-100 rounded"><ArrowUp size={14} /></button>
                <button onClick={() => moveCol(cIdx, 1)} className="p-2 hover:bg-gray-100 rounded"><ArrowDown size={14} /></button>
                <button onClick={() => removeCol(cIdx)} className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
              {col.type === 'links' && (
                <div className="ml-4 space-y-2">
                  {(col.links || []).map((l, lIdx) => (
                    <div key={lIdx} className="flex gap-2 items-center">
                      <input type="text" value={l.label || ''} onChange={(e) => updateColLink(cIdx, lIdx, { label: e.target.value })} placeholder="Label" className="border rounded px-2 py-1 text-sm flex-1" />
                      <input type="text" value={l.href || ''} onChange={(e) => updateColLink(cIdx, lIdx, { href: e.target.value })} placeholder="URL or #" className="border rounded px-2 py-1 text-sm flex-1" />
                      <button onClick={() => removeColLink(cIdx, lIdx)} className="text-red-600"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button onClick={() => addColLink(cIdx)} className="text-[#C9A86C] hover:underline text-xs">+ Add Link</button>
                </div>
              )}
              {col.type !== 'links' && <p className="ml-4 text-xs text-gray-500">Auto-pulled from Restaurant Info.</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Bottom Links</h3>
          <button onClick={addBottom} className="text-[#C9A86C] hover:underline text-sm">+ Add</button>
        </div>
        <div className="space-y-2">
          {bottomLinks.map((l, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input type="text" value={l.label} onChange={(e) => updateBottom(idx, { label: e.target.value })} placeholder="Label" className="border rounded px-2 py-1 text-sm flex-1" />
              <input type="text" value={l.href} onChange={(e) => updateBottom(idx, { href: e.target.value })} placeholder="URL" className="border rounded px-2 py-1 text-sm flex-1" />
              <button onClick={() => removeBottom(idx)} className="text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="bg-[#C9A86C] text-white px-6 py-3 rounded-lg flex items-center gap-2 hover:bg-[#B8956A] disabled:opacity-50">
        <Save size={18} /> {saving ? 'Saving…' : 'Save Footer'}
      </button>
    </div>
  );
};

export default FooterManager;
